import { AI_SMS } from '../../lib/botName';
import { formatSpeed } from '../../lib/leads/leadList';
import type { LeadFunnel } from '../../types/leadList';

// THE LEAD FUNNEL CARD (Speed to Lead Stage 4.5C): Leads -> Contacted (split by how) -> Committed -> Joined, plus the median
// speed to lead. Presentational: the host fetches the figures (computed server-side from the lead records) and passes them in.
//
// ⚠️ Every step shows its COUNT; a percentage of leads is shown only when there are leads to divide by (a dash, never 0%).
// ⚠️ A capped read says so: the figures are then a floor, and the card does not imply they are complete.

const pct = (n: number, d: number): string => (d > 0 ? `${Math.round((n / d) * 100)}%` : '—');

function Step({ label, value, of, sub, testId }: { label: string; value: number; of: number; sub?: string; testId: string }) {
  return (
    <div className="min-w-[8rem] flex-1 rounded border border-border bg-background p-3" data-testid={testId}>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label === 'Leads' ? 'in this period' : `${pct(value, of)} of leads`}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export function LeadFunnelCard({ funnel }: { funnel: LeadFunnel }) {
  const f = funnel;
  return (
    <section className="space-y-3 rounded border border-border bg-card p-3" aria-label="Lead funnel" data-testid="lead-funnel">
      <div className="flex flex-wrap gap-3">
        <Step testId="funnel-leads" label="Leads" value={f.leads} of={f.leads} />
        <Step testId="funnel-contacted" label="Contacted" value={f.contacted} of={f.leads} sub={`${f.contactedViaSam} via ${AI_SMS} · ${f.contactedViaCall} via call`} />
        <Step testId="funnel-committed" label="Committed" value={f.committed} of={f.leads} sub="Call back or tour booked, or joined" />
        <Step testId="funnel-joined" label="Joined" value={f.joined} of={f.leads} />
        <div className="min-w-[8rem] flex-1 rounded border border-border bg-background p-3" data-testid="funnel-speed">
          <p className="text-xs font-medium text-muted-foreground">Median speed to lead</p>
          <p className="text-2xl font-semibold text-foreground">{formatSpeed(f.medianSpeedSeconds)}</p>
          <p className="text-xs text-muted-foreground">{f.respondedCount > 0 ? `over ${f.respondedCount} responded to` : 'nobody responded to yet'}</p>
        </div>
      </div>
      {f.capped && (
        <p className="text-xs text-amber-700" data-testid="funnel-capped">
          This period holds more leads than the funnel reads, so these figures are a minimum. Narrow the dates for exact figures.
        </p>
      )}
    </section>
  );
}
